// process-balance Web Component - Balance subtitle line lengths
import template from './process-balance.html';
import styles from './process-balance.less';
import './doc-editor';
import { apiClient } from '../services/api-client';
import { FileReaderUtil } from '../services/file-reader';
export class ProcessBalance extends HTMLElement {
    constructor() {
        super(...arguments);
        this.state = {
            fileName: null,
            fileContent: null,
            validationResult: null,
            balanceResult: null,
            isValidating: false,
            isProcessing: false
        };
        this.elements = {};
    }
    connectedCallback() {
        this.render();
        this.cacheElements();
        this.initializeEventListeners();
    }
    render() {
        const styleEl = document.createElement('style');
        styleEl.textContent = styles;
        this.appendChild(styleEl);
        const container = document.createElement('div');
        container.innerHTML = template;
        this.appendChild(container);
    }
    cacheElements() {
        this.elements.uploadArea = this.querySelector('#uploadArea');
        this.elements.fileInput = this.querySelector('#fileInput');
        this.elements.optionsPanel = this.querySelector('#optionsPanel');
        this.elements.currentFile = this.querySelector('#currentFile');
        this.elements.maxLineLength = this.querySelector('#maxLineLength');
        this.elements.allowLongerSecondLine = this.querySelector('#allowLongerSecondLine');
        this.elements.validateBtn = this.querySelector('#validateBtn');
        this.elements.balanceBtn = this.querySelector('#balanceBtn');
        this.elements.downloadBtn = this.querySelector('#downloadBtn');
        this.elements.messagesContainer = this.querySelector('#messagesContainer');
        this.elements.editorCard = this.querySelector('#editorCard');
        this.elements.placeholderCard = this.querySelector('#placeholderCard');
        this.elements.docEditor = this.querySelector('#docEditor');
    }
    initializeEventListeners() {
        this.elements.uploadArea?.addEventListener('click', () => {
            this.elements.fileInput?.click();
        });
        this.elements.fileInput?.addEventListener('change', (e) => {
            const input = e.target;
            if (input.files && input.files[0]) {
                this.handleFileUpload(input.files[0]);
            }
        });
        // Drag and drop
        this.elements.uploadArea?.addEventListener('dragover', (e) => {
            e.preventDefault();
            this.elements.uploadArea?.classList.add('drag-over');
        });
        this.elements.uploadArea?.addEventListener('dragleave', () => {
            this.elements.uploadArea?.classList.remove('drag-over');
        });
        this.elements.uploadArea?.addEventListener('drop', (e) => {
            e.preventDefault();
            this.elements.uploadArea?.classList.remove('drag-over');
            if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
                this.handleFileUpload(e.dataTransfer.files[0]);
            }
        });
        this.elements.validateBtn?.addEventListener('click', () => this.handleValidate());
        this.elements.balanceBtn?.addEventListener('click', () => this.handleBalance());
        this.elements.downloadBtn?.addEventListener('click', () => this.handleDownload());
    }
    async handleFileUpload(file) {
        try {
            // Validate format
            const format = FileReaderUtil.detectFormat(file.name);
            if (format !== 'AvidDS') {
                alert('Please upload an Avid DS file (.ds or .ds.txt)');
                return;
            }
            // Validate size
            if (!FileReaderUtil.validateFileSize(file, 50)) {
                alert(`File too large. Maximum size is 50MB.`);
                return;
            }
            const content = await FileReaderUtil.readAsText(file);
            this.state.fileName = file.name;
            this.state.fileContent = content;
            this.showOptionsPanel();
            this.elements.currentFile.textContent = file.name;
            this.showEditor();
            this.elements.docEditor?.setValue(content);
        }
        catch (error) {
            console.error('File upload error:', error);
            alert(`Failed to read file: ${error}`);
        }
    }
    showOptionsPanel() {
        this.elements.uploadArea.style.display = 'none';
        this.elements.optionsPanel.style.display = 'block';
    }
    showEditor() {
        this.elements.placeholderCard.style.display = 'none';
        this.elements.editorCard.style.display = 'block';
    }
    async handleValidate() {
        if (!this.state.fileContent) {
            alert('Please upload a file first');
            return;
        }
        this.setButtonLoading(this.elements.validateBtn, true);
        try {
            const result = await apiClient.validateAvidDS({
                content: this.state.fileContent,
                fileName: this.state.fileName || undefined
            });
            this.state.validationResult = result;
            if (result.messages.length > 0) {
                this.displayMessages(result.messages);
                this.elements.docEditor?.highlightMessages(result.messages);
            }
            else {
                alert('Document is valid!');
            }
        }
        catch (error) {
            console.error('Validation error:', error);
            alert(`Validation failed: ${error}`);
        }
        finally {
            this.setButtonLoading(this.elements.validateBtn, false);
        }
    }
    async handleBalance() {
        if (!this.state.fileContent || !this.state.fileName) {
            alert('Please upload a file first');
            return;
        }
        this.setButtonLoading(this.elements.balanceBtn, true);
        try {
            const content = this.elements.docEditor?.getValue() || this.state.fileContent;
            const result = await apiClient.balance({
                content,
                fileName: this.state.fileName,
                options: {
                    maxLineLength: parseInt(this.elements.maxLineLength.value) || 38,
                    allowLongerSecondLine: this.elements.allowLongerSecondLine.checked
                }
            });
            this.state.balanceResult = result;
            if (result.success && result.outputContent) {
                this.elements.docEditor?.setValue(result.outputContent);
                this.elements.downloadBtn.style.display = 'block';
                if (result.messages.length > 0) {
                    this.displayMessages(result.messages);
                }
                else {
                    alert('Lines balanced successfully!');
                }
            }
            else {
                this.displayMessages(result.messages);
            }
        }
        catch (error) {
            console.error('Balance error:', error);
            alert(`Balance failed: ${error}`);
        }
        finally {
            this.setButtonLoading(this.elements.balanceBtn, false);
        }
    }
    handleDownload() {
        const result = this.state.balanceResult;
        if (!result || !result.success || !result.outputContent) {
            alert('No balanced content available for download');
            return;
        }
        const fileName = result.outputFileName || 'balanced.ds.txt';
        const blob = new Blob([result.outputContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
    displayMessages(messages) {
        if (!messages || messages.length === 0) {
            this.elements.messagesContainer.style.display = 'none';
            return;
        }
        this.elements.messagesContainer.style.display = 'block';
        let html = '<div class="alert alert-info"><h6>Messages:</h6><ul class="mb-0">';
        messages.forEach(msg => {
            html += `<li>${msg.message}${msg.context ? ` (${msg.context})` : ''}</li>`;
        });
        html += '</ul></div>';
        this.elements.messagesContainer.innerHTML = html;
    }
    setButtonLoading(button, loading) {
        const spinner = button.querySelector('.spinner-border');
        if (loading) {
            button.disabled = true;
            spinner?.classList.remove('d-none');
        }
        else {
            button.disabled = false;
            spinner?.classList.add('d-none');
        }
    }
}
customElements.define('process-balance', ProcessBalance);
